class BoxrHeader extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-boxr-menu-open]');
    this.drawer = this.querySelector('[data-boxr-drawer]');
    this.closeButtons = this.querySelectorAll('[data-boxr-menu-close]');

    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();

    if (!this.toggle || !this.drawer) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.toggle.addEventListener('click', this.open);
    this.closeButtons.forEach((button) => button.addEventListener('click', this.close));
    document.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.handleScroll);
    if (!this.toggle) return;
    this.toggle.removeEventListener('click', this.open);
    this.closeButtons.forEach((button) => button.removeEventListener('click', this.close));
    document.removeEventListener('keydown', this.handleKeydown);
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.classList.add('boxr-menu-open');
    this.drawer.querySelector('[data-boxr-menu-close]')?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('boxr-menu-open');
    this.previouslyFocused?.focus();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
  }

  handleScroll() {
    this.classList.toggle('is-scrolled', window.scrollY > 18);
  }
}

if (!customElements.get('boxr-header')) customElements.define('boxr-header', BoxrHeader);

const boxrReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const boxrDesktopMotion = window.matchMedia('(min-width: 990px)');
const boxrParallaxItems = new Set();
let boxrParallaxFrame;
let boxrParallaxListening = false;

function updateBoxrParallax() {
  boxrParallaxFrame = null;

  if (boxrReducedMotion.matches || !boxrDesktopMotion.matches) {
    boxrParallaxItems.forEach((element) => element.style.setProperty('--boxr-parallax-y', '0px'));
    return;
  }

  const viewportHeight = window.innerHeight;
  boxrParallaxItems.forEach((element) => {
    const rect = element.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > viewportHeight) return;
    const distance = viewportHeight / 2 - (rect.top + rect.height / 2);
    const offset = Math.max(-12, Math.min(12, distance * 0.035));
    element.style.setProperty('--boxr-parallax-y', `${offset.toFixed(2)}px`);
  });
}

function requestBoxrParallax() {
  if (!boxrParallaxFrame) boxrParallaxFrame = window.requestAnimationFrame(updateBoxrParallax);
}

function initializeBoxrMotion(root = document) {
  root.querySelectorAll('[data-boxr-parallax]').forEach((element) => boxrParallaxItems.add(element));

  if (boxrParallaxItems.size && !boxrParallaxListening) {
    window.addEventListener('scroll', requestBoxrParallax, { passive: true });
    window.addEventListener('resize', requestBoxrParallax, { passive: true });
    boxrParallaxListening = true;
    requestBoxrParallax();
  }

  root.querySelectorAll('.boxr-hero:not([data-boxr-motion-ready])').forEach((hero) => {
    hero.dataset.boxrMotionReady = 'true';
    let pointerFrame;

    const updatePointer = (event) => {
      if (boxrReducedMotion.matches || !boxrDesktopMotion.matches) return;
      const rect = hero.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - 0.5) * -8;
      const y = ((event.clientY - rect.top) / rect.height - 0.5) * -6;
      window.cancelAnimationFrame(pointerFrame);
      pointerFrame = window.requestAnimationFrame(() => {
        hero.style.setProperty('--boxr-pointer-x', `${x.toFixed(2)}px`);
        hero.style.setProperty('--boxr-pointer-y', `${y.toFixed(2)}px`);
      });
    };

    const resetPointer = () => {
      hero.style.setProperty('--boxr-pointer-x', '0px');
      hero.style.setProperty('--boxr-pointer-y', '0px');
    };

    hero.addEventListener('pointermove', updatePointer, { passive: true });
    hero.addEventListener('pointerleave', resetPointer, { passive: true });
  });
}

window.addEventListener('DOMContentLoaded', () => initializeBoxrMotion());

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeBoxrMotion(event.target));
}
